import type { Object3D } from 'three';
import {
	isSketchArcObject,
	isSketchEllipseObject,
	isSketchLineObject,
	isSketchSlotObject,
	isSketchSplineObject,
	isSketchStyleSplineObject,
} from '../../../mesh/guards';
import { SketchArcObject } from '../../../mesh/sketch/SketchArcObject';
import { SketchEllipseObject } from '../../../mesh/sketch/SketchEllipseObject';
import { SketchLineObject } from '../../../mesh/sketch/SketchLineObject';
import type { SketchPointObject } from '../../../mesh/sketch/SketchPointObject';
import {
	SketchBezierCurveClosureKind,
	SketchSplineObject,
} from '../../../mesh/sketch/SketchSplineObject';
import { SketchStyleSplineObject } from '../../../mesh/sketch/SketchStyleSplineObject';
import type { ExternalGeometrySourceDescriptor } from '../model/ExternalGeometrySourceDescriptor';
import type { ProjectionResult } from '../model/ProjectionResult';
import type { TargetAnchorBindings } from '../model/TargetAnchorBindings';
import { resolveSketchSlotEdgeByIndex } from './resolveSketchSlotEdgeByIndex';
import type {
	EndpointBindings,
	SketchReferenceTargetAnchorBindingCollectorInput,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceTargetAnchorBindingCollector';

export type {
	SketchReferenceTargetAnchorBindingCollectorInput,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceTargetAnchorBindingCollector';

/**
 * 草图引用目标锚点绑定收集器。
 * 说明：
 * 1. 统一负责“源端点 -> 目标控制点”的拓扑映射收集；
 * 2. create 与 rebuild 都走同一个 collector，避免两条链路出现不同的端点口径；
 * 3. 只收集具备端点语义的曲线对象，闭合实体没有共享端点时不会产生额外绑定；
 * 4. 相同来源锚点由端点拓扑统一器映射为一个目标点，不同锚点之间的显式关系由关系恢复服务处理。
 */
export class SketchReferenceTargetAnchorBindingCollector {
	/**
	 * 收集一批目标实体的锚点绑定。
	 *
	 * @param input 收集输入。
	 * @returns 源锚点到目标点的绑定表。
	 */
	public collect(
		input: SketchReferenceTargetAnchorBindingCollectorInput,
	): TargetAnchorBindings {
		const targetAnchorBindings: TargetAnchorBindings = new Map();

		input.items.forEach((item) => {
			this.collectForItem({
				projectionResult: item.projectionResult,
				targetEntity: item.targetEntity,
				resolveSourceObject: input.resolveSourceObject,
				targetAnchorBindings,
			});
		});

		return targetAnchorBindings;
	}

	/**
	 * 为单个目标实体收集锚点绑定。
	 *
	 * @param input 单项收集输入。
	 */
	private collectForItem(input: {
		projectionResult: ProjectionResult;
		targetEntity: Object3D;
		resolveSourceObject?: (descriptor: ExternalGeometrySourceDescriptor) => Object3D | null;
		targetAnchorBindings: TargetAnchorBindings;
	}): void {
		const targetEndpoints = this.resolveTargetEndpointBindings(input.targetEntity);
		if (!targetEndpoints) {
			return;
		}

		const endpointAnchorBindings = input.projectionResult.endpointAnchorBindings ?? [];
		if (endpointAnchorBindings.length > 0) {
			endpointAnchorBindings.forEach((binding) => {
				this.appendTargetAnchorBinding(
					input.targetAnchorBindings,
					binding.sourceAnchorId,
					binding.targetEndpoint === 'start'
						? targetEndpoints.startPoint
						: targetEndpoints.endPoint,
				);
			});
			return;
		}

		if (!input.resolveSourceObject || input.projectionResult.sourceDescriptor.kind !== 'sketch-entity') {
			return;
		}

		const sourceObject = input.resolveSourceObject(input.projectionResult.sourceDescriptor);
		if (!sourceObject) {
			return;
		}

		const sourceEndpoints = this.resolveSourceEndpointBindings(
			sourceObject,
			input.projectionResult.sourceDescriptor,
		);
		if (!sourceEndpoints) {
			return;
		}

		this.appendTargetAnchorBinding(
			input.targetAnchorBindings,
			sourceEndpoints.startPoint.uuid,
			targetEndpoints.startPoint,
		);
		this.appendTargetAnchorBinding(
			input.targetAnchorBindings,
			sourceEndpoints.endPoint.uuid,
			targetEndpoints.endPoint,
		);
	}

	/**
	 * 解析源实体可用于拓扑恢复的端点。
	 * 说明：
	 * 1. 这里使用源对象自身保存的点对象 uuid 作为 anchor；
	 * 2. 若两个源实体共用同一个 SketchPointObject，转换批次会自然落入同一个 anchor 组；
	 * 3. 相同来源点 UUID 的目标端点会在关系恢复前统一为同一个共享点对象。
	 *
	 * @param sourceObject 源实体。
	 * @returns 起终点绑定；不存在时返回 null。
	 */
	private resolveSourceEndpointBindings(
		sourceObject: Object3D,
		sourceDescriptor: ExternalGeometrySourceDescriptor,
	): EndpointBindings | null {
		if (
			sourceDescriptor.kind === 'sketch-entity'
			&& sourceDescriptor.subEntity?.kind === 'slot-edge'
		) {
			if (!isSketchSlotObject(sourceObject)) {
				return null;
			}
			const sourceEdge = resolveSketchSlotEdgeByIndex(
				sourceObject,
				sourceDescriptor.subEntity.edgeIndex,
			);
			return sourceEdge ? this.resolveSourceEndpointBindings(sourceEdge, {
				kind: 'sketch-entity',
				sourceSketchId: sourceDescriptor.sourceSketchId,
				entityId: sourceEdge.persistentId,
			}) : null;
		}

		if (isSketchLineObject(sourceObject)) {
			return {
				startPoint: sourceObject.startPoint,
				endPoint: sourceObject.endPoint,
			};
		}

		if (isSketchArcObject(sourceObject) || isSketchEllipseObject(sourceObject)) {
			return {
				startPoint: sourceObject.getStartPoint(),
				endPoint: sourceObject.getEndPoint(),
			};
		}

		if (isSketchSplineObject(sourceObject) || isSketchStyleSplineObject(sourceObject)) {
			/**
			 * 闭合样条没有可与相邻开放曲线共享的起终端点；其闭合由周期曲线定义表达。
			 * 若仍把同一个来源点映射为两个目标样条端点，canonicalizer 会把样条内部闭合
			 * 误判为跨实体共享拓扑，并要求不存在的 replacement keeper。
			 */
			if (isSketchSplineObject(sourceObject)
				&& sourceObject.getCurveMetadata().closureKind === SketchBezierCurveClosureKind.Closed) {
				return null;
			}
			const controlPoints = sourceObject.getControlPoints();
			const startPoint = controlPoints[0];
			const endPoint = controlPoints[controlPoints.length - 1];
			if (!startPoint || !endPoint) {
				return null;
			}
			return { startPoint, endPoint };
		}

		return null;
	}

	/**
	 * 解析目标实体可承接端点拓扑的控制点。
	 *
	 * @param targetEntity 目标实体。
	 * @returns 起终点绑定；不存在时返回 null。
	 */
	public resolveTargetEndpointBindings(
		targetEntity: Object3D,
	): EndpointBindings | null {
		if (targetEntity instanceof SketchLineObject) {
			return {
				startPoint: targetEntity.startPoint,
				endPoint: targetEntity.endPoint,
			};
		}

		if (targetEntity instanceof SketchArcObject || targetEntity instanceof SketchEllipseObject) {
			return {
				startPoint: targetEntity.getStartPoint(),
				endPoint: targetEntity.getEndPoint(),
			};
		}

		if (targetEntity instanceof SketchSplineObject || targetEntity instanceof SketchStyleSplineObject) {
			// 闭合样条的首尾是同一个周期点，不对外暴露起终锚点。
			// 在目标边界拦截可同时覆盖 sketch fallback 与 topology 显式 endpointAnchorBindings。
			if (targetEntity instanceof SketchSplineObject
				&& targetEntity.getCurveMetadata().closureKind === SketchBezierCurveClosureKind.Closed) {
				return null;
			}
			const controlPoints = targetEntity.getControlPoints();
			const startPoint = controlPoints[0];
			const endPoint = controlPoints[controlPoints.length - 1];
			if (!startPoint || !endPoint) {
				return null;
			}
			return { startPoint, endPoint };
		}

		return null;
	}

	/**
	 * 向锚点绑定表追加一个目标点。
	 *
	 * @param targetAnchorBindings 目标锚点绑定表。
	 * @param sourceAnchorId 源锚点 id。
	 * @param targetPoint 目标点对象。
	 */
	private appendTargetAnchorBinding(
		targetAnchorBindings: TargetAnchorBindings,
		sourceAnchorId: string,
		targetPoint: SketchPointObject,
	): void {
		const boundTargets = targetAnchorBindings.get(sourceAnchorId) ?? [];
		boundTargets.push(targetPoint);
		targetAnchorBindings.set(sourceAnchorId, boundTargets);
	}
}
